import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/axios";
import AppModalShell from "../common/AppModalShell";
import { useTranslation } from "react-i18next";
import PremiumCodeLabel from "../common/PremiumCodeLabel";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";

/**
 * HostClaimModal
 *
 * Модальное окно для ХОСТА: показывает данные участника и события,
 * считанные из токена, и даёт кнопку подтверждения.
 *
 * Ожидается, что родитель передаст `token` (из URL или сканера)
 * и смонтирует модалку поверх карточки события.
 */
const HostClaimModal = ({
  visible = false,
  token,                // строка токена из QR
  onClose,              // () => void
  onSuccess,            // () => void (после успешного confirm)
}) => {
  const { t } = useTranslation("events");

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [data, setData] = useState(null); // { event, participant, already_attended, confirmable }
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const activeTokenRef = useRef(null);

  useEffect(() => {
    activeTokenRef.current = visible && token ? token : null;
    if (!visible || !token) return;

    // The native app keeps this component mounted between consecutive QR
    // scans. Never carry the previous participant's success/data state into
    // the next claim.
    setData(null);
    setErrorMsg("");
    setSubmitting(false);
    setSubmitted(false);
  }, [visible, token]);

  // Вспомогательная маска для аватарки
  const initials = useMemo(() => {
    const name = data?.participant?.username || "?";
    const parts = String(name).trim().split(/\s+/);
    const first = parts[0]?.[0] || "?";
    const second = parts[1]?.[0] || "";
    return (first + second).toUpperCase();
  }, [data]);

  // Загрузка claim-информации
  useEffect(() => {
    if (!visible || !token) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        const { data } = await api.get(`events/attendance/claim/${token}/`);
        if (!cancelled) setData(data);
      } catch (e) {
        const msg =
          e?.response?.data?.error ||
          e?.message ||
          t("host_claim_modal.errors.load_failed");
        if (!cancelled) setErrorMsg(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [visible, token, t]);

  const alreadyAttended = !!data?.already_attended;
  const requiresPrepayment = !!data?.requires_prepayment;
  const isPaid = !!data?.paid;
  const usedMultisport = data?.used_multisport;
  const ticketPriceSnapshot = data?.ticket_price_snapshot;

  const handleMarkPaidAndConfirm = async () => {
    if (!token || submitting || submitted) return;
    const claimToken = token;
    setSubmitting(true);
    try {
      const { data: resp } = await api.post(
        `events/attendance/claim/${claimToken}/confirm/`,
        { mark_paid: true }
      );
      if (activeTokenRef.current !== claimToken) return;
      setSubmitted(true);
      // Optimistically update local payment/attendance flags
      setData((prev) =>
        prev
          ? {
              ...prev,
              paid: true,
              already_attended: true,
            }
          : prev
      );
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: resp?.already_attended
              ? t("host_claim_modal.events.already_confirmed")
              : t(
                  "host_claim_modal.events.payment_marked_and_confirmed"
                ),
          },
        })
      );
      onSuccess?.(resp);
    } catch (e) {
      if (activeTokenRef.current !== claimToken) return;
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        t("host_claim_modal.errors.confirm_failed");
      setErrorMsg(msg);
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg } })
      );
      setSubmitted(false);
    } finally {
      if (activeTokenRef.current === claimToken) {
        setSubmitting(false);
      }
    }
  };

  const handleConfirm = async () => {
    if (!token || submitting || submitted) return;
    const claimToken = token;
    setSubmitting(true);
    try {
      const { data: resp } = await api.post(
        `events/attendance/claim/${claimToken}/confirm/`
      );
      if (activeTokenRef.current !== claimToken) return;
      setSubmitted(true);
      setData((prev) =>
        prev ? { ...prev, already_attended: true } : prev
      );
      window.dispatchEvent(
        new CustomEvent("global-info", {
          detail: {
            message: resp?.already_attended
              ? t("host_claim_modal.events.already_confirmed")
              : t("host_claim_modal.events.attendee_confirmed"),
          },
        })
      );
      onSuccess?.(resp);
    } catch (e) {
      if (activeTokenRef.current !== claimToken) return;
      const msg =
        e?.response?.data?.error ||
        e?.message ||
        t("host_claim_modal.errors.confirm_failed");
      setErrorMsg(msg);
      window.dispatchEvent(
        new CustomEvent("global-error", { detail: { message: msg } })
      );
    } finally {
      if (activeTokenRef.current === claimToken) {
        setSubmitting(false);
      }
    }
  };

  if (!visible) return null;

  const submitButtonClassName = `flex h-12 w-full items-center justify-center rounded-full px-5 text-[16px] font-semibold leading-none transition active:scale-[0.985] disabled:cursor-not-allowed ${
    submitted || alreadyAttended
      ? "bg-emerald-500 text-white"
      : submitting
      ? "bg-white/16 text-white/42"
      : "bg-white text-[#2563EB] shadow-[0_10px_28px_rgba(0,0,0,0.18)]"
  }`;

  const submitButton = requiresPrepayment && !isPaid ? (
    <button
      type="button"
      onClick={handleMarkPaidAndConfirm}
      disabled={submitting || submitted || alreadyAttended}
      className={submitButtonClassName}
    >
      {submitted || alreadyAttended
        ? t("host_claim_modal.buttons.confirmed")
        : submitting
        ? t("host_claim_modal.buttons.confirming")
        : t("host_claim_modal.buttons.mark_paid_and_confirm")}
    </button>
  ) : (
    <button
      type="button"
      onClick={handleConfirm}
      disabled={submitting || submitted || alreadyAttended}
      className={submitButtonClassName}
    >
      {submitted || alreadyAttended
        ? t("host_claim_modal.buttons.confirmed")
        : submitting
        ? t("host_claim_modal.buttons.confirming")
        : t("host_claim_modal.buttons.confirm")}
    </button>
  );

  return (
    <AppModalShell
      open={visible}
      title={t("host_claim_modal.header.title")}
      description={t("host_claim_modal.header.subtitle")}
      onClose={onClose}
      closeAriaLabel={t("host_claim_modal.header.close_aria")}
      zIndexClassName="z-[30000]"
      maxWidthClassName="max-w-[32rem]"
      footer={submitButton}
      descriptionClassName="text-center"
      closeOnBackdropClick={false}
      rootStyle={{ zIndex: 2147483647 }}
      forcePortalToBody
    >
      <div className="space-y-4">
        {errorMsg && (
          <p className="text-center text-red-400 text-sm">
            {errorMsg}
          </p>
        )}

        {loading ? (
          <p className="text-center text-white/70">
            {t("host_claim_modal.body.loading")}
          </p>
        ) : data ? (
          <div className="rounded-[18px] border border-white/10 bg-white/[0.07] p-4">
            <div className="text-white/70 text-xs">
              {t("host_claim_modal.body.event_label")}
            </div>
            <div className="text-white text-base font-semibold">
              {data.event?.name}
            </div>
            {data.event?.date_time && (
              <div className="text-white/60 text-xs mt-0.5">
                {new Date(data.event.date_time).toLocaleString()}
              </div>
            )}

            <div className="mt-4 text-white/70 text-xs">
              {t("host_claim_modal.body.participant_label")}
            </div>
            <div className="mt-1 flex items-center gap-3">
              {resolveUserAvatarSrc(data.participant) ? (
                <img
                  src={resolveUserAvatarSrc(data.participant)}
                  alt={t("host_claim_modal.body.avatar_alt")}
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-white/80 text-sm font-bold">
                  {initials}
                </div>
              )}
              <div className="min-w-0">
                <div className="text-white font-semibold truncate">
                  {data.participant?.username}
                </div>
                {data.participant?.code && (
                  <div className="text-white/60 text-xs">
                    {t("host_claim_modal.body.code_label")}{" "}
                    <PremiumCodeLabel
                      code={data.participant.code}
                      isPremium={Boolean(data?.participant?.has_premium_subscription)}
                    />
                  </div>
                )}
              </div>
            </div>

            {requiresPrepayment && (
              <div className="mt-3">
                <div className="text-white/70 text-xs">
                  {t("host_claim_modal.body.ticket_payment_label")}
                </div>
                <div
                  className={`text-sm font-semibold ${
                    isPaid ? "text-emerald-400" : "text-red-400"
                  }`}
                >
                  {isPaid
                    ? t("host_claim_modal.body.paid_status")
                    : t("host_claim_modal.body.not_paid_status")}
                </div>
              </div>
            )}
            {ticketPriceSnapshot ? (
              <div className="mt-3">
                <div className="text-white/70 text-xs">
                  {t("host_claim_modal.body.ticket_price_snapshot_label")}
                </div>
                <div className="text-sm font-semibold text-white">
                  {ticketPriceSnapshot}
                </div>
              </div>
            ) : null}
            {usedMultisport === true || usedMultisport === false ? (
              <div className="mt-3">
                <div className="text-white/70 text-xs">
                  {t("host_claim_modal.body.pricing_option_label")}
                </div>
                <div className="text-sm font-semibold text-white">
                  {usedMultisport
                    ? t("host_claim_modal.body.pricing_option_multisport")
                    : t("host_claim_modal.body.pricing_option_regular")}
                </div>
              </div>
            ) : null}

            <div className="mt-3">
              {alreadyAttended ? (
                <div className="text-amber-300 text-sm">
                  {t("host_claim_modal.body.already_attended")}
                </div>
              ) : (
                <div className="text-white/80 text-sm">
                  {requiresPrepayment && !isPaid
                    ? t("host_claim_modal.body.cash_hint")
                    : t("host_claim_modal.body.not_confirmed_yet")}
                </div>
              )}
            </div>
          </div>
        ) : (
          <p className="text-center text-white/70">
            {t("host_claim_modal.body.no_data")}
          </p>
        )}
      </div>
    </AppModalShell>
  );
};

export default HostClaimModal;
